import {
  isProductCategoryId,
  systemCategoryIdsSet,
} from '@shared/models/element-categories.model';
import {
  PIECES_QUANTITY_FACTORY,
  PIECES_QUANTITY_NAME,
} from '@shared/models/element-quantities.model';
import type { IElementProperty } from '@shared/models/element-property.model';
import type {
  IElement,
  OneOfChildElements,
  Project,
} from '@shared/models/project.model';
import type { Recipe } from '@shared/models/recipe.model';
import { getElementCategoryId } from './element-category-id.helpers';
import {
  getElementProperties,
  hasCount,
  isElementExpressionProperty,
} from './element-property.helpers';
import {
  getElementQuantityRecord,
  isElementQuantityExpressionProperty,
  setElementQuantityProperties,
} from './element-quantity.helpers';
import { isElement } from './recursive-element.helpers';

export type PiecesPropertyOccurrence = {
  hostId: string;
  hostName?: string;
  propertyId: string;
  unit?: string;
  countExpression?: string;
};

export type SyncPiecesQuantityOptions = {
  /**
   * When true, hide a custom `pieces` property, copy its count onto the
   * quantity row, and set {@link IElement.selectedQuantity} to pieces.
   */
  absorbExistingProperty: boolean;
};

/**
 * True when the category template includes {@link PIECES_QUANTITY_NAME}.
 */
export const categoryUsesPiecesQuantity = (element: IElement): boolean => {
  const categoryId = getElementCategoryId(element);
  return (
    isProductCategoryId(categoryId) || systemCategoryIdsSet.has(categoryId)
  );
};

/**
 * True when a non-hidden custom property is named {@link PIECES_QUANTITY_NAME}.
 */
export const hasVisiblePiecesProperty = (parent: {
  properties?: IElementProperty[];
}): boolean =>
  getElementProperties(parent).some(
    (property) => property.name === PIECES_QUANTITY_NAME && !property.hidden,
  );

/**
 * Build a preview row for a custom `pieces` property, if one exists.
 */
export const getPiecesPropertyOccurrence = (
  host: { id: string; name?: string },
  parent: { properties?: IElementProperty[] },
): PiecesPropertyOccurrence | undefined => {
  const property = getElementProperties(parent).find(
    (item) => item.name === PIECES_QUANTITY_NAME,
  );
  if (!property) return undefined;
  return {
    hostId: host.id,
    hostName: host.name,
    propertyId: property.id,
    unit: 'unit' in property ? property.unit : undefined,
    countExpression: isElementExpressionProperty(property)
      ? property.count?.expression
      : undefined,
  };
};

/**
 * Add or normalize the hidden inheritable pieces quantity on a category element.
 */
export const ensurePiecesQuantity = (element: IElement): IElement => {
  if (!categoryUsesPiecesQuantity(element)) return element;
  if (hasVisiblePiecesProperty(element)) return element;

  const existing = getElementQuantityRecord(element)[PIECES_QUANTITY_NAME];
  if (
    isElementQuantityExpressionProperty(existing) &&
    existing.hidden === true &&
    existing.inheritFallback === true &&
    existing.unit === 'pcs'
  ) {
    return element;
  }

  return setElementQuantityProperties(element, {
    [PIECES_QUANTITY_NAME]: {
      ...PIECES_QUANTITY_FACTORY,
      unit: 'pcs',
    },
  });
};

/**
 * Hide a visible custom pieces property. Does not touch quantity or selectedQuantity.
 */
const hideVisiblePiecesProperty = <
  T extends { properties?: IElementProperty[] },
>(
  parent: T,
): T => {
  const properties = getElementProperties(parent);
  let changed = false;
  const nextProperties = properties.map((property) => {
    if (property.name !== PIECES_QUANTITY_NAME || property.hidden) {
      return property;
    }
    changed = true;
    return { ...property, hidden: true };
  });
  return changed ? { ...parent, properties: nextProperties } : parent;
};

/**
 * Hide a custom pieces property, copy its count onto the quantity, and select it.
 *
 * A hidden property still copies and selects when it has a count and
 * quantity.pieces does not (first apply after migration). A later sync does
 * not overwrite a quantity that already has a count or reset {@link IElement.selectedQuantity}.
 */
const absorbPiecesProperty = (element: IElement): IElement => {
  const piecesProperty = getElementProperties(element).find(
    (property) => property.name === PIECES_QUANTITY_NAME,
  );
  if (!piecesProperty) return ensurePiecesQuantity(element);

  const wasVisible = !piecesProperty.hidden;
  let next = ensurePiecesQuantity(hideVisiblePiecesProperty(element));

  const piecesQuantity = getElementQuantityRecord(next)[PIECES_QUANTITY_NAME];
  const quantityHasCount =
    isElementQuantityExpressionProperty(piecesQuantity) &&
    hasCount(piecesQuantity);
  const canCopyCount =
    isElementExpressionProperty(piecesProperty) &&
    hasCount(piecesProperty) &&
    !quantityHasCount;
  if (canCopyCount) {
    next = setElementQuantityProperties(next, {
      [PIECES_QUANTITY_NAME]: { count: piecesProperty.count },
    });
  }

  // Visible properties always select pieces. A hidden one selects only when
  // its count was just copied, so a re-run cannot reset the list unit.
  const shouldSelect =
    next.selectedQuantity !== PIECES_QUANTITY_NAME &&
    (wasVisible || canCopyCount);
  if (!shouldSelect) return next;
  return { ...next, selectedQuantity: PIECES_QUANTITY_NAME };
};

/**
 * Sync pieces quantity on one element. Collects a custom-property occurrence when present.
 */
export const syncPiecesQuantityOnElement = (
  element: IElement,
  options: SyncPiecesQuantityOptions,
): { element: IElement; occurrence?: PiecesPropertyOccurrence } => {
  const occurrence = getPiecesPropertyOccurrence(element, element);
  if (options.absorbExistingProperty) {
    return { element: absorbPiecesProperty(element), occurrence };
  }
  return { element: ensurePiecesQuantity(element), occurrence };
};

/**
 * True when a custom property is named {@link PIECES_QUANTITY_NAME}.
 */
const hasPiecesProperty = (parent: {
  properties?: IElementProperty[];
}): boolean =>
  getElementProperties(parent).some(
    (property) => property.name === PIECES_QUANTITY_NAME,
  );

/**
 * Fold a recipe `pieces` property into {@link PIECES_QUANTITY_NAME} after recipe
 * properties were merged onto the element.
 *
 * The first fold absorbs a visible or already-hidden property (migration hides
 * recipe pieces before the user applies them). A later apply only hides the
 * re-injected property so it cannot reset {@link IElement.selectedQuantity}.
 */
export const syncPiecesQuantityAfterRecipeApply = (
  elementBeforeApply: IElement,
  applied: IElement,
): IElement => {
  if (hasPiecesProperty(elementBeforeApply)) {
    return ensurePiecesQuantity(hideVisiblePiecesProperty(applied));
  }
  return syncPiecesQuantityOnElement(applied, {
    absorbExistingProperty: hasPiecesProperty(applied),
  }).element;
};

/**
 * Sync pieces quantity through an element tree.
 */
export const syncPiecesQuantityInElements = (
  elements: OneOfChildElements[],
  options: SyncPiecesQuantityOptions,
): {
  elements: OneOfChildElements[];
  changed: boolean;
  occurrences: PiecesPropertyOccurrence[];
} => {
  const occurrences: PiecesPropertyOccurrence[] = [];
  let changed = false;

  const mapTree = (nodes: OneOfChildElements[]): OneOfChildElements[] => {
    let listChanged = false;
    const mapped = nodes.map((node) => {
      if (!isElement(node)) return node;
      const mappedChildren = mapTree(node.elements);
      const base =
        mappedChildren === node.elements
          ? node
          : { ...node, elements: mappedChildren };
      const synced = syncPiecesQuantityOnElement(base, options);
      if (synced.occurrence) occurrences.push(synced.occurrence);
      if (synced.element !== base) {
        listChanged = true;
        changed = true;
        return synced.element;
      }
      if (base !== node) {
        listChanged = true;
        changed = true;
        return base;
      }
      return node;
    });
    return listChanged ? mapped : nodes;
  };

  const nextElements = mapTree(elements);
  return { elements: nextElements, changed, occurrences };
};

/**
 * Hide a custom pieces property on a recipe document (no quantity record on recipes).
 */
const hideRecipePiecesProperty = (
  recipe: Recipe,
): { recipe: Recipe; changed: boolean } => {
  const next = hideVisiblePiecesProperty(recipe);
  return next === recipe
    ? { recipe, changed: false }
    : { recipe: next, changed: true };
};

/**
 * Sync pieces quantity on a recipe (root properties + child elements).
 */
export const syncPiecesQuantityInRecipe = (
  recipe: Recipe,
  options: SyncPiecesQuantityOptions,
): {
  recipe: Recipe;
  changed: boolean;
  occurrences: PiecesPropertyOccurrence[];
} => {
  const recipeOccurrence = getPiecesPropertyOccurrence(recipe, recipe);
  const occurrences: PiecesPropertyOccurrence[] = recipeOccurrence
    ? [recipeOccurrence]
    : [];

  let next = recipe;
  let changed = false;

  if (options.absorbExistingProperty && recipeOccurrence) {
    const hidden = hideRecipePiecesProperty(next);
    next = hidden.recipe;
    changed = hidden.changed || changed;
  }

  const children = syncPiecesQuantityInElements(next.elements, options);
  occurrences.push(...children.occurrences);
  if (children.changed) {
    next = { ...next, elements: children.elements };
    changed = true;
  }

  return { recipe: next, changed, occurrences };
};

/**
 * Sync pieces quantity on every baseline element tree in a project.
 */
export const syncPiecesQuantityInProject = (
  project: Project,
  options: SyncPiecesQuantityOptions,
): {
  project: Project;
  changed: boolean;
  occurrences: PiecesPropertyOccurrence[];
} => {
  const occurrences: PiecesPropertyOccurrence[] = [];
  let changed = false;
  const baselines = project.baselines.map((version) => {
    const result = syncPiecesQuantityInElements(version.elements, options);
    occurrences.push(...result.occurrences);
    if (!result.changed) return version;
    changed = true;
    return { ...version, elements: result.elements };
  });
  return {
    project: changed ? { ...project, baselines } : project,
    changed,
    occurrences,
  };
};
